import { useMemo } from 'react';
import { Trans, useTranslation } from 'next-i18next';
import styled from 'styled-components';

import { Button, Typography, TitleTypography } from '@design-system/components';
import useWindowResize from '@source/hooks/useWindowResize';
import { URL_CONFIG } from '@source/constants/urlConfig';
import { IRegionProps } from '@source/interface';

import { getCCPOQueryStringUrl } from '@source/pages/CarroCertified/utils';

const StyledWrapper = styled.div<{ isMYChinese?: boolean; isJPJa?: boolean }>`
  padding-top: ${(props) => (props.isMYChinese ? '70px' : '32px')};
  ${(props) => props.theme.typo.familyGoogle.regular}

  h1 {
    margin-bottom: 0;
  }
  .heading-mobile {
    display: flex;
    flex-direction: column;
    .heading-heading,
    .heading-title-v2 {
      display: inline;
      font-size: 25px;
      line-height: 29px;
    }

    .heading-title {
      display: inline;
      margin-left: 4px;
    }
  }

  .heading-heading {
    display: block;
    color: ${(props) => props.theme.color.primaryBase};
  }

  .heading-title,
  .heading-title-v2 {
    display: block;
    color: ${(props) => props.theme.color.onPrimary};
    margin-bottom: 12px;
  }

  .heading-description {
    color: ${(props) => props.theme.color.onPrimary};
    margin-bottom: 20px;
  }

  .heading-description-v2 {
    color: ${(props) => props.theme.color.onPrimary};
    margin-bottom: ${(props) => (props.isMYChinese ? '28px' : '20px')};
  }

  @media screen and (min-width: 1280px) {
    padding-top: 48px;
    padding-top: 159px;
  }

  @media only screen and (max-width: 575px) {
    .heading-description {
      font-size: 9px;
      margin-bottom: 15px;
    }

    .heading-description-v2 {
      font-weight: 400;
      font-size: 9px;
      line-height: 15px;
    }
  }
`;
const StyledButton = styled(Button)<{ marginTop: number }>`
  color: ${(props) => props.theme.color.primaryBase};
  font-size: 14px;
  line-height: 22px;
  > span {
    color: ${(props) => props.theme.color.onPrimary};
  }

  padding: 10px 20px;

  @media only screen and (max-width: 575px) {
    padding: 6.5px 24px;
    margin-top: ${(props) => props.marginTop}px;
  }
`;

const CCPOHeading = ({ region }: IRegionProps) => {
  const { i18n, t } = useTranslation('ccpo');
  const [width] = useWindowResize();
  const isMYChinese = i18n.language === 'zh-MY';
  const isJPJa = i18n.language === 'ja';

  const headingSize = useMemo(() => {
    if (width > 992) {
      return 'xlHeader';
    }

    if (width > 576) {
      return 'lHeader';
    }
    return 'mHeader';
  }, [width]);

  return (
    <StyledWrapper isMYChinese={isMYChinese}>
      {headingSize === 'mHeader' ? (
        <TitleTypography className="heading-mobile">
          <Typography className="heading-heading" variant={headingSize}>
            <Trans
              ns="ccpo"
              defaults={t(['my', 'jp'].includes(region) ? 'ccpo.banner.headingV2' : 'ccpo.banner.heading')}
              components={{
                color: <span style={{ color: '#fff' }} />,
                specificFont: <span style={{ fontFamily: 'Poppins' }} />,
              }}
            />
          </Typography>{' '}
          <Typography
            className={['my', 'jp', 'hk'].includes(region) ? 'heading-title-v2' : 'heading-title'}
            variant={headingSize}
          >
            <Trans
              ns="ccpo"
              defaults={t(
                ['my', 'jp', 'hk'].includes(region) ? 'ccpo.banner.titleMobileV2' : 'ccpo.banner.titleMobile',
              )}
              components={{ br: <br />, specificFont: <span style={{ fontFamily: 'Poppins' }} /> }}
            />
          </Typography>
        </TitleTypography>
      ) : (
        <TitleTypography>
          <Typography className="heading-heading" variant={headingSize}>
            <Trans
              ns="ccpo"
              defaults={t(['my', 'jp'].includes(region) ? 'ccpo.banner.headingV2' : 'ccpo.banner.heading')}
              components={{
                color: <span style={{ color: '#fff' }} />,
                specificFont: <span style={{ fontFamily: 'Poppins' }} />,
              }}
            />
          </Typography>{' '}
          <Typography className="heading-title" variant={headingSize}>
            <Trans
              ns="ccpo"
              defaults={t(['my', 'jp'].includes(region) ? 'ccpo.banner.titleV2' : 'ccpo.banner.title')}
              components={{ br: <br />, specificFont: <span style={{ fontFamily: 'Poppins' }} /> }}
            />
          </Typography>
        </TitleTypography>
      )}
      <p className={['my', 'jp'].includes(region) ? 'heading-description-v2' : 'heading-description'}>
        <Trans
          ns="ccpo"
          defaults={t(headingSize === 'mHeader' ? 'ccpo.banner.descriptionMobile' : 'ccpo.banner.description')}
          components={{ br: <br />, specificFont: <span style={{ fontFamily: 'Poppins' }} /> }}
        />
      </p>
      <div>
        <a
          href={
            URL_CONFIG({
              region,
              lang: i18n.language,
              query: getCCPOQueryStringUrl(region),
            }).formatted
          }
        >
          <StyledButton marginTop={isJPJa ? 40 : 0}>
            <span>
              <Trans ns="ccpo" defaults={t(region === 'my' ? 'ccpo.banner.buttonMY' : 'ccpo.banner.button')} />
            </span>
          </StyledButton>
        </a>
      </div>
    </StyledWrapper>
  );
};

export default CCPOHeading;
